import type { Operation } from 'fast-json-patch'
import { ModerationStatus, RelationStatus } from '@roadfootage/api-client'
import type { StaffIncidentReportRelationDto } from '@roadfootage/api-client'

export type RelationDecision = 'keep' | 'reject'

export interface RelationReview {
  /** The relation to PATCH. */
  id: string
  operations: Operation[]
}

/**
 * Staff's decisions on the pending relations of an incident under review (RF-133), one PATCH per relation.
 *
 * - Only Pending relations: an approved or rejected one is never touched by a plain edit.
 * - Reject: `Rejected`, whether or not the report is being approved.
 * - Keep, while approving the report: `Approved`, but only when the other report is already Approved, so the report being
 *   approved completes the pair. Re-approving a report whose partner is still pending leaves the relation pending.
 *   Keeping without approving also leaves it pending.
 */
export function relationReviews(
  relations: StaffIncidentReportRelationDto[],
  decisions: Record<string, RelationDecision>,
  approving: boolean
): RelationReview[] {
  const reviews: RelationReview[] = []
  for (const relation of relations) {
    if (!relation.id || relation.status !== RelationStatus.Pending) continue
    const decision = decisions[relation.id] ?? 'keep'
    if (decision === 'reject') {
      reviews.push({
        id: relation.id,
        operations: [{ op: 'replace', path: '/status', value: RelationStatus.Rejected }]
      })
    } else if (approving && relation.otherModerationStatus === ModerationStatus.Approved) {
      reviews.push({
        id: relation.id,
        operations: [{ op: 'replace', path: '/status', value: RelationStatus.Approved }]
      })
    }
  }
  return reviews
}

/** "1 min", "45 min", "3 h", "2 h 5 min": the size of a time gap, never negative. */
export function formatGap(minutes: number): string {
  const total = Math.round(Math.abs(minutes))
  if (total < 60) return `${total} min`
  const hours = Math.floor(total / 60)
  const rest = total % 60
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`
}
